/**
 * @module Utils.EnvironmentService
 */

function createEnvironmentService(util, _window) {
    let _sessionId;
    let _oauthToken;
    let _apiUrlBase;
    let _webUrlBase;
    let _webSocketBase;
    let _partnerId;
    let _locale;
    let _audioWorkerUrl;
    let _libopusScriptUrl;
    let _client;
    let _auth;

    const authType = Object.freeze({
        sessionId: 'sessionId',
        bearer: 'bearer',
        seedonkSession: 'seedonkSession'
    });

    /**
     * @description ensure all string properties of the input have been
     *     scrubbed of characters that could be used in hacking attempts
     *     and that all strings are a reasonable length.
     * @param {Object|String|Array} value
     * @returns {Object|String|Array}
     */
    function scrubClientData(value) {
        if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
            Object.keys(value).forEach(key => {
                value[key] = scrubClientData(value[key]);
            });
            return value;
        }
        if (typeof value === 'string' && value !== '') {
            return util.removeInvalidCharacters(value).substring(0, 30);
        }
        if (Array.isArray(value)) {
            let i = 0;
            while (i < value.length) {
                if (typeof value[i] === 'string' && value[i] !== '') {
                    value[i] = util.removeInvalidCharacters(value[i]).substring(0, 30);
                }
                i += 1;
            }
            return value;
        }
        return value;
    }

    function calcClientInfo(bowser) {
        const info = scrubClientData(bowser.parse(_window.navigator.userAgent));
        const platformMap = bowser.PLATFORMS_MAP ?? {};
        const osMap = bowser.OS_MAP ?? {};
        const browserMap = bowser.BROWSER_MAP ?? {};
        const engineMap = bowser.ENGINE_MAP ?? {};
        return Object.freeze(util.merge(info, {
            platformEnum: platformMap,
            osEnum: osMap,
            browserEnum: browserMap,
            engineEnum: engineMap,
            ios: Boolean(info?.os?.name === osMap.iOS || (_window?.webkit?.messageHandlers)),
            android: Boolean(info?.os?.name === osMap.Android || _window.SeedonkAndroid),
            windows: Boolean(info?.os?.name === osMap.Windows),
            mac: Boolean(info?.os?.name === osMap.MacOS),
            tablet: Boolean(info?.platform?.type === platformMap.tablet),
            mobile: Boolean(info?.platform?.type === platformMap.mobile),
            isEmbedded: function () {
                return Boolean(_window.SeedonkAndroid || (_window?.webkit?.messageHandlers));
            }
        }));
    }

    const _environmentService = {
        get auth() {
            return _auth;
        },
        set auth(value) {
            _auth = value;
        },

        get sessionId() { return _sessionId; },
        set sessionId(value) { _sessionId = value; },

        get oauthToken() { return _oauthToken; },
        set oauthToken(value) { _oauthToken = value; },

        get apiUrlBase() {
            if (_apiUrlBase) {
                return _apiUrlBase;
            }
            return _window?.location?.host ? ('https://' + _window.location.host + '/cxs/api') : '';
        },
        set apiUrlBase(value) { _apiUrlBase = value; },

        get webUrlBase() {
            if (_webUrlBase) {
                return _webUrlBase;
            }
            return _window?.location?.host ? ('https://' + _window.location.host + '/app') : '';
        },
        set webUrlBase(value) { _webUrlBase = value; },

        get webSocketBase() {
            if (_webSocketBase) {
                return _webSocketBase;
            }
            return _window?.location?.host ? ('wss://' + _window.location.host + ':443') : '';
        },
        set webSocketBase(value) { _webSocketBase = value; },

        get audioWorkerUrl() {
            if (_audioWorkerUrl) {
                return _audioWorkerUrl;
            }
            return _window?.location?.host ? ('https://' + _window.location.host + '/app/lib/sdk/audioWorker.js') : '';
        },
        set audioWorkerUrl(value) { _audioWorkerUrl = value; },

        get libopusScriptUrl() {
            if (_libopusScriptUrl) {
                return _libopusScriptUrl;
            }
            return _window?.location?.host ? ('https://' + _window.location.host + '/app/lib/sdk/libopus.wasm') : '';
        },
        set libopusScriptUrl(value) { _libopusScriptUrl = value; },

        get partnerId() { return _partnerId; },
        set partnerId(value) {
            _partnerId = util.removeInvalidCharacters(value).substring(0, 30);
        },

        get locale() {
            if (_locale) {
                return _locale;
            }
            return 'en-US';
        },
        set locale(value) {
            _locale = util.removeInvalidCharacters(value).substring(0, 20);
        },

        get client() {
            if (!_client && _window.bowser) {
                _client = calcClientInfo(_window.bowser);
            }
            return _client;
        },

        get authType() {
            return authType;
        }
    };
    return _environmentService;
}

export default createEnvironmentService;


